import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { setAuth } from '@/lib/auth';
import { AlertCircle } from 'lucide-react';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '146961377362-7m6ji9dsjl9gakocejhqu0o0m690scih.apps.googleusercontent.com';

export default function GoogleSignInButton({ disabled = false, onSuccess }) {
  const buttonRef = useRef(null);
  const isInitialized = useRef(false);
  const [error, setError] = useState(null);

  // Get API URL dynamically
  const getApiUrl = () => {
    const currentHost = window.location.origin;
    if (currentHost.includes('localhost')) {
      return process.env.REACT_APP_BACKEND_URL + '/api';
    }
    return currentHost + '/api';
  };

  useEffect(() => {
    const initializeGoogleSignIn = () => {
      if (!window.google || !window.google.accounts) {
        console.log('Google Sign In not ready yet, retrying...');
        setTimeout(initializeGoogleSignIn, 500);
        return;
      }

      if (isInitialized.current) return;
      isInitialized.current = true;

      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        if (buttonRef.current) {
          window.google.accounts.id.renderButton(
            buttonRef.current,
            {
              theme: 'outline',
              size: 'large',
              width: buttonRef.current.offsetWidth,
              text: 'signin_with',
              shape: 'rectangular',
            }
          );
        }

        console.log('Google Sign In initialized successfully');
      } catch (error) {
        console.error('Error initializing Google Sign In:', error);
        setError('Google Sign-In unavailable');
      }
    };

    setTimeout(initializeGoogleSignIn, 500);
  }, []);

  const handleCredentialResponse = async (response) => {
    console.log('Google credential received');
    setError(null);
    
    try {
      const API_URL = getApiUrl();
      const backendResponse = await axios.post(`${API_URL}/auth/google/callback`, {
        credential: response.credential,
      });

      const { session_token, ...user } = backendResponse.data;
      setAuth(session_token, user);
      
      toast.success(`Welcome, ${user.name}!`);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Google sign in error:', error);
      const errorDetail = error.response?.data?.detail || 'Google sign in failed';
      
      if (errorDetail.includes('audience') || errorDetail.includes('Invalid token')) {
        setError('Google Sign-In not configured for this domain. Please use email/password.');
        toast.error('Please use email/password login', { duration: 5000 });
      } else {
        toast.error(errorDetail);
      }
    }
  };

  if (error) {
    return (
      <div className="w-full p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
        <div className="flex items-center justify-center gap-2 text-yellow-700 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={buttonRef}
      className="w-full h-12 flex items-center justify-center"
      style={{ minHeight: '48px' }}
    />
  );
}
